import type { ThreadPhaseInventoryPhase } from '../api/types';

/**
 * One labelled block of prompts shown under a phase header.
 *
 * `label` is null when the phase asked everyone the same thing — the common
 * case, rendered exactly as a bare list. Once role variants exist every block
 * carries a label, because "the prompts this phase asked" is no longer one list.
 */
export interface PhasePromptGroup {
  label: string | null;
  prompts: string[];
}

/** Label for the base prompts once role variants sit beside them. */
const SHARED_PROMPT_LABEL = 'Everyone';

function cleanPrompts(prompts: readonly string[] | null | undefined): string[] {
  return (prompts ?? []).filter((prompt) => typeof prompt === 'string' && prompt.trim().length > 0);
}

/**
 * The prompts a phase actually asked, grouped for display.
 *
 * Role-aware phases (The Big Day's Couple / Wedding Party, Deep Dive's
 * Conductor / Participant) serve `rolePromptVariants[role]` *in place of* the
 * base prompts, so rendering only `prompts` would show text no role-assigned
 * contributor was ever given. Contributors with no resolved role still get the
 * base prompts, so those stay — labelled — rather than being replaced.
 */
export function resolvePhasePromptGroups(
  phase: Pick<ThreadPhaseInventoryPhase, 'prompts' | 'rolePromptVariants'> | undefined,
): PhasePromptGroup[] {
  if (!phase) return [];

  const shared = cleanPrompts(phase.prompts);
  const roleGroups = Object.entries(phase.rolePromptVariants ?? {})
    .map(([role, prompts]) => ({ label: role, prompts: cleanPrompts(prompts) }))
    .filter((group) => group.prompts.length > 0);

  if (roleGroups.length === 0) {
    return shared.length > 0 ? [{ label: null, prompts: shared }] : [];
  }

  return shared.length > 0
    ? [{ label: SHARED_PROMPT_LABEL, prompts: shared }, ...roleGroups]
    : roleGroups;
}

/**
 * Fold a second phase's prompt groups into the first's.
 *
 * `templatePhaseKey` is the phase name capped at 50 chars server-side, so two
 * phases whose names share that prefix collapse onto one key — and their takes
 * merge into one section. Overwriting would then head the merged section with
 * only the last phase's prompts while positioning it by the first's canonical
 * index, silently hiding a question contributors answered. Concatenate instead,
 * dropping blocks that are byte-identical (the same label carrying the same
 * prompts) so an unchanged prompt set doesn't render twice.
 */
export function mergePhasePromptGroups(
  existing: PhasePromptGroup[],
  incoming: PhasePromptGroup[],
): PhasePromptGroup[] {
  // NUL/SOH separators rather than JSON, which would only dedupe groups whose
  // keys happen to be in the same order. Postgres rejects embedded NUL bytes in
  // text columns, so neither byte can appear in a stored prompt or role name —
  // the same sentinel argument contributionThreading.ts relies on.
  const identity = (group: PhasePromptGroup) => `${group.label ?? ''}\u0000${group.prompts.join('\u0001')}`;
  const seen = new Set(existing.map(identity));
  const additions = incoming.filter((group) => !seen.has(identity(group)));
  return additions.length > 0 ? [...existing, ...additions] : existing;
}
